import { useEffect, useMemo, useState } from "react";
import { I18n } from "react-redux-i18n";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useUnmount } from "react-use";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import FormButton from "./FormButton";
import { Auth } from "aws-amplify";
import { setUserProfile } from "../../state/actions/user";
import { connect } from "react-redux";
import { normalizeUser } from "../../utils/normalizeUser";
import {
  AUTHENTICATOR_FORGOT,
  AUTHENTICATOR_RESETNEW,
  AUTHENTICATOR_SIGNUP,
  AUTHENTICATOR_VERIFY,
} from "./constants";

const Signin = ({
  initialValue,
  setInitialValue,
  setAuthScreen,
  setUserProfile,
  setUser,
}) => {
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(false);

  const schema = useMemo(() => {
    return yup.object({
      email: yup
        .string()
        .required(I18n.t("authenticator.required"))
        .email(I18n.t("authenticator.emailRequired")),
      password: yup.string().required(I18n.t("authenticator.required")),
    });
  }, []);

  const {
    reset,
    handleSubmit,
    control,
    getValues,
    formState: { errors },
    formState: { isSubmitted },
  } = useForm({
    defaultValues: {
      email: initialValue.email,
      password: initialValue.password,
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset({
      email: initialValue.email,
      password: initialValue.password,
    });
  }, [initialValue, reset]);

  useUnmount(() => {
    setInitialValue({ ...initialValue, ...getValues() });
  });

  const onSubmit = async (data) => {
    setError(false);
    setLoader(true);

    try {
      const user = await Auth.signIn(data.email, data.password);

      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        setUser(user);
        return setAuthScreen(AUTHENTICATOR_RESETNEW);
      }

      setUserProfile(normalizeUser(user));

      return;
    } catch (e) {
      console.log(e);

      if (
        e.code === "NotAuthorizedException" ||
        e.code === "UserNotFoundException"
      )
        setError(true);

      if (e.code === "UserNotConfirmedException")
        return setAuthScreen(AUTHENTICATOR_VERIFY);
    }

    setLoader(false);
  };

  return (
    <Grid
      container
      spacing={2}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Typography
          variant="h4"
          color="primary"
          style={{
            fontFamily: "'Paytone One', sans- serif",
          }}
        >
          {I18n.t("authenticator.signIn")}
        </Typography>
      </Grid>

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Controller
          name="email"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              label={I18n.t("authenticator.email")}
              variant="outlined"
              {...field}
              error={(isSubmitted && Boolean(errors.email)) || error}
              helperText={errors?.email?.message || ""}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Controller
          name="password"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              label={I18n.t("authenticator.password")}
              variant="outlined"
              type={showPassword ? "text" : "password"}
              {...field}
              error={(isSubmitted && Boolean(errors.password)) || error}
              helperText={errors?.password?.message || ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>

      {error && (
        <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
          <Alert severity="error">
            {I18n.t("authenticator.NotAuthorizedException")}
          </Alert>
        </Grid>
      )}

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Button
          color="primary"
          style={{ alignSelf: "center" }}
          onClick={() => setAuthScreen(AUTHENTICATOR_FORGOT)}
        >
          {I18n.t("authenticator.forgotPassword")}
        </Button>
      </Grid>

      <Box style={{ alignSelf: "center", margin: "20px auto" }}>
        <FormButton {...{ label: I18n.t("authenticator.signIn"), loader }} />
      </Box>

      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography style={{ marginRight: "4px" }} color="textSecondary">
          {I18n.t("authenticator.notMember")}
        </Typography>

        <Button
          type="submit"
          color="primary"
          onClick={() => setAuthScreen(AUTHENTICATOR_SIGNUP)}
        >
          {I18n.t("authenticator.signUp")}
        </Button>
      </Grid>
    </Grid>
  );
};

const mapDispatchToProps = (dispatch) => ({
  setUserProfile: (user) => dispatch(setUserProfile(user)),
});

export default connect(null, mapDispatchToProps)(Signin);
